// frontend/packages/business-console/src/pages/Pulse.jsx
//
// "Pulse" — the high-value dashboard / heads-up display for the
// Console. One screen of KPIs across every operating plane of the
// business: money, pipeline, customers, inbox, AI visibility, tasks,
// website performance.
//
// Per the locked design (2026-05-26):
//   - Lives at /pulse — NOT replacing Home.jsx (the mobile-friendly
//     tile launcher stays as the home page).
//   - Numbers + lists first, no third-party charting library. Charts
//     can land as a follow-up.
//   - Frontend-only demo mode via `?demo=1` so marketing screenshots
//     don't require touching Joshua's live business data. See
//     `Pulse/usePulseData.js` for the wiring.
//
// Backend aggregation endpoints don't exist yet — under the non-demo
// path the page renders empty-state copy in each section. The hook
// will gain a real fetch branch when the backend endpoints land.

import React, { useContext, useEffect, useState } from 'react';
import { Box, Chip, Container, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { BusinessContext } from '../contexts/BusinessContext';
import { Link as RouterLink } from 'react-router-dom';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import { usePulseData } from './Pulse/usePulseData';
import { listByStatus } from '../lib/outboxStore';
import { useTokens, StatusPill } from '../components/primitives';
import MoneySection from './Pulse/sections/MoneySection';
import PipelineSection from './Pulse/sections/PipelineSection';
import CrmSection from './Pulse/sections/CrmSection';
import VisibilitySection from './Pulse/sections/VisibilitySection';
import TasksSection from './Pulse/sections/TasksSection';
import VisitorsSection from './Pulse/sections/VisitorsSection';
// Inbox + Performance Pulse cards have no backend aggregation yet, so they're
// not rendered (they'd be permanently empty). Wire them when the endpoints
// land. Inbox-on-Pulse can reuse the existing inbox summary as a quick win.

function greeting() {
  const hour = new Date().getHours();
  if (hour < 5) return 'Up late';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Up late';
}

function firstName(user) {
  if (!user) return '';
  const raw =
    user.firstName ||
    user.givenName ||
    user.name ||
    user.displayName ||
    user.email ||
    '';
  return String(raw).split(/[\s@]/)[0] || '';
}

export default function Pulse() {
  const { user } = useAuth();
  const { business } = useContext(BusinessContext);
  const { data, isDemo } = usePulseData();
  const t = useTokens();

  const name = firstName(user);
  const businessName =
    (business && (business.displayName || business.name)) || '';
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  // 2.19 outbox visibility — surface pending+failed drafts so the
  // operator notices on the Pulse landing screen, not buried in an
  // editor. Polls every 10s as a cheap belt-and-suspenders (the
  // editor pages own the authoritative `online` event listener; this
  // is just a header read-out).
  const [outboxCounts, setOutboxCounts] = useState({ pending: 0, failed: 0 });
  useEffect(() => {
    if (isDemo) return undefined;
    let cancelled = false;
    const refresh = async () => {
      const [quotes, invoices, forms] = await Promise.all([
        listByStatus({ kind: 'quote', status: ['pending', 'failed'] }),
        listByStatus({ kind: 'invoice', status: ['pending', 'failed'] }),
        // 5.2 — workflow-form submissions queued offline by the
        // (future) renderer surface here too.
        listByStatus({
          kind: 'workflow-form',
          status: ['pending', 'failed'],
        }),
      ]);
      if (cancelled) return;
      const all = [...quotes, ...invoices, ...forms];
      setOutboxCounts({
        pending: all.filter((it) => it.status === 'pending').length,
        failed: all.filter((it) => it.status === 'failed').length,
      });
    };
    refresh();
    const id = setInterval(refresh, 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isDemo]);
  const outboxTotal = outboxCounts.pending + outboxCounts.failed;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 5 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 28 },
              fontWeight: 900,
              letterSpacing: '-0.8px',
              lineHeight: 1,
              color: t.text,
            }}
          >
            {greeting()}
            {name ? `, ${name}` : ''}.
          </Typography>
          <Typography
            sx={{ color: t.muted, mt: 1, fontSize: 14, fontWeight: 500 }}
          >
            Here&apos;s the pulse of{' '}
            <Box component="span" sx={{ fontWeight: 700, color: t.text }}>
              {businessName || 'your business'}
            </Box>{' '}
            today.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {outboxTotal > 0 ? (
            <Chip
              size="small"
              color={outboxCounts.failed > 0 ? 'error' : 'warning'}
              variant="outlined"
              icon={<CloudOffOutlinedIcon fontSize="small" />}
              label={
                outboxCounts.failed > 0
                  ? `${outboxCounts.failed} draft${outboxCounts.failed === 1 ? '' : 's'} need${outboxCounts.failed === 1 ? 's' : ''} attention`
                  : `${outboxCounts.pending} draft${outboxCounts.pending === 1 ? '' : 's'} pending sync`
              }
              clickable
              component={RouterLink}
              to={`${basePath}/quotes/drafts`}
              sx={{ fontWeight: 600 }}
            />
          ) : null}
          {isDemo ? <StatusPill kind="info" label="Demo data" /> : null}
        </Box>
      </Box>

      {/* Redesign layout: money hero leads (cash leads), then a 2x2 glance
          grid (pipeline, tasks, crm, visitors), then the visibility card
          full width. Each card owns its own height. */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2 } }}
      >
        <MoneySection money={data.money} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 2, sm: 2 },
            alignItems: 'stretch',
          }}
        >
          <PipelineSection pipeline={data.pipeline} />
          <TasksSection tasks={data.tasks} />
          <CrmSection crm={data.crm} />
          <VisitorsSection visitors={data.visitors} />
        </Box>

        <VisibilitySection visibility={data.visibility} />
      </Box>
    </Container>
  );
}
